import React, { useCallback, useEffect, useMemo, useState, useImperativeHandle, forwardRef } from 'react';
import toast from 'react-hot-toast';
import {
  incomeExpenseApi,
  IeCategory,
  ProjectBankAccount,
  ProjectLedgerFilters,
  ProjectLedgerResponse,
  ProjectLedgerRow,
} from '../../../api/incomeExpenseApi';
import { feeCategoriesApi, FeeCategory } from '../../../api/feeCategoriesApi';
import { printVoucherHtml } from '../../../utils/voucherPrint';
import { getCalendarMonthInputRange } from '../../../utils/apartmentDate';
import { exportProjectAccountLedgerReport } from '../projectAccountLedgerExport';
import ProjectBankSubLedgerPanel from './ProjectBankSubLedgerPanel';

const formatCurrency = (n: number) => `${new Intl.NumberFormat('vi-VN').format(Math.round(n))}đ`;

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const VoucherNameCell: React.FC<{
  name: string;
  receiptRef?: string | null;
}> = ({ name, receiptRef }) => (
  <div className="leading-snug">
    <div>{name}</div>
    {receiptRef && <div className="font-medium text-gray-900">{receiptRef}</div>}
  </div>
);

const CARD_ISSUANCE_FEE_CODE_ORDER = ['THE001', 'THE002', 'THE003'];

const isCardIssuanceBillableFeeCode = (feeCode?: string | null) => {
  const code = (feeCode ?? '').trim().toUpperCase();
  return /^THE\d{3}$/.test(code);
};

const dedupeCategories = (categories: IeCategory[]) => {
  const byName = new Map<string, IeCategory>();
  for (const category of categories) {
    const existing = byName.get(category.name);
    if (!existing || (category.isSystem && !existing.isSystem)) {
      byName.set(category.name, category);
    }
  }
  return Array.from(byName.values());
};

const AmountCell: React.FC<{ value: number; variant?: 'income' | 'expense' }> = ({
  value,
  variant,
}) => {
  if (!value) return <span className="text-gray-400">0đ</span>;
  const color = variant === 'income' ? 'text-cyan-600' : variant === 'expense' ? 'text-red-600' : 'text-gray-900';
  return <span className={color}>{formatCurrency(value)}</span>;
};

const LedgerVoucherModal: React.FC<{
  entryId: string;
  title: string;
  mode?: 'fee' | 'surplus';
  onClose: () => void;
}> = ({ entryId, title, mode, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setFailed(false);
      setHtmlContent('');
      try {
        const html = await incomeExpenseApi.fetchLedgerVoucherHtml(entryId, mode);
        if (!cancelled) setHtmlContent(html);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [entryId, mode]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => htmlContent && printVoucherHtml(htmlContent)}
              disabled={!htmlContent}
              className="text-sm text-cyan-600 hover:text-cyan-700 disabled:text-gray-400"
            >
              In
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl leading-none px-2"
              aria-label="Đóng"
            >
              ×
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100 relative" style={{ minHeight: '70vh' }}>
          {loading && !failed && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
              <span className="ml-2 text-gray-600 text-sm">Đang tải...</span>
            </div>
          )}
          {failed ? (
            <div className="flex items-center justify-center py-16 text-gray-500 text-sm">
              Không thể tải phiếu
            </div>
          ) : htmlContent ? (
            <iframe
              title={title}
              srcDoc={htmlContent}
              className="w-full border-0 bg-white"
              style={{ minHeight: '70vh', display: 'block' }}
              sandbox="allow-same-origin allow-scripts"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

const resolveVoucherTitle = (row: ProjectLedgerRow): string => {
  if (row.viewType === 'fee_receipt') return 'Phiếu thu chi (Quản lý thu chi)';
  if (row.viewType === 'offset_voucher') return 'Cấn trừ phiếu thu';
  if (row.viewType === 'surplus_voucher' && row.voucherName === 'Nhận tiền từ chuyển khoản online') {
    return 'Nhận tiền từ chuyển khoản online';
  }
  if (row.viewType === 'surplus_voucher') return 'Đóng dư phiếu thu';
  if (row.viewType === 'manual_voucher') return 'Phiếu thu/chi tiện ích';
  return 'Xem phiếu';
};

export interface ProjectAccountLedgerDetailHandle {
  exportReport: () => Promise<void>;
}

interface ProjectAccountLedgerDetailProps {
  account: ProjectBankAccount;
  buildingId: string;
  buildingName?: string;
  onBack: () => void;
}

export const ProjectAccountLedgerDetail = forwardRef<
  ProjectAccountLedgerDetailHandle,
  ProjectAccountLedgerDetailProps
>(function ProjectAccountLedgerDetail({ account, buildingId, buildingName = '', onBack }, ref) {
  const [detailSection, setDetailSection] = useState<'ledger' | 'subledger'>('ledger');
  const initialRange = useMemo(() => getCalendarMonthInputRange(), []);
  const [filters, setFilters] = useState<ProjectLedgerFilters>(initialRange);
  const [draftFilters, setDraftFilters] = useState<ProjectLedgerFilters>(initialRange);
  const [data, setData] = useState<ProjectLedgerResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [revenueCategories, setRevenueCategories] = useState<IeCategory[]>([]);
  const [expenditureCategories, setExpenditureCategories] = useState<IeCategory[]>([]);
  const [cardIssuanceFeeCategories, setCardIssuanceFeeCategories] = useState<FeeCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [voucherPreview, setVoucherPreview] = useState<{
    entryId: string;
    title: string;
    mode?: 'fee' | 'surplus';
  } | null>(null);

  useEffect(() => {
    if (!buildingId) return;
    Promise.all([
      incomeExpenseApi.listRevenueCategories(buildingId),
      incomeExpenseApi.listExpenditureCategories(buildingId),
      feeCategoriesApi.getAll(buildingId),
    ])
      .then(([revenue, expenditure, feeCategories]) => {
        setRevenueCategories(revenue);
        setExpenditureCategories(expenditure);
        const cardFees = feeCategories
          .filter((fc) => fc.isActive && isCardIssuanceBillableFeeCode(fc.feeCode))
          .sort(
            (a, b) =>
              CARD_ISSUANCE_FEE_CODE_ORDER.indexOf((a.feeCode ?? '').toUpperCase()) -
              CARD_ISSUANCE_FEE_CODE_ORDER.indexOf((b.feeCode ?? '').toUpperCase()),
          );
        setCardIssuanceFeeCategories(cardFees);
      })
      .catch(() => toast.error('Không thể tải danh mục loại thu/chi'));
  }, [buildingId]);

  const loadLedger = useCallback(async () => {
    setLoading(true);
    try {
      setData(await incomeExpenseApi.listProjectBankAccountLedger(account.id, filters));
    } catch {
      toast.error('Không thể tải chi tiết thu chi');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [account.id, filters]);

  useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  const categoryOptions = useMemo(() => {
    const revenueOpts = dedupeCategories(revenueCategories).map((c) => ({
      key: `revenue:${c.id}`,
      id: c.id,
      name: c.name,
      kind: 'revenue' as const,
    }));
    const feeOpts = cardIssuanceFeeCategories.map((fc) => ({
      key: `fee:${fc.id}`,
      id: fc.id,
      name: fc.name,
      kind: 'fee' as const,
    }));
    const expenditureOpts = dedupeCategories(expenditureCategories).map((c) => ({
      key: `expenditure:${c.id}`,
      id: c.id,
      name: c.name,
      kind: 'expenditure' as const,
    }));
    const merged = [...revenueOpts, ...feeOpts, ...expenditureOpts];
    const seen = new Set<string>();
    return merged.filter((item) => {
      if (seen.has(item.name)) return false;
      seen.add(item.name);
      return true;
    });
  }, [revenueCategories, expenditureCategories, cardIssuanceFeeCategories]);

  const applyFilters = () => {
    const next: ProjectLedgerFilters = { ...draftFilters };
    if (selectedCategory) {
      const selected = categoryOptions.find((c) => c.key === selectedCategory);
      next.revenueCategoryId = selected?.kind === 'revenue' ? selected.id : undefined;
      next.feeCategoryId = selected?.kind === 'fee' ? selected.id : undefined;
      next.expenditureCategoryId = selected?.kind === 'expenditure' ? selected.id : undefined;
    } else {
      next.revenueCategoryId = undefined;
      next.feeCategoryId = undefined;
      next.expenditureCategoryId = undefined;
    }
    setFilters(next);
  };

  const summary = data?.summary;
  const periodSummary = data?.periodSummary ?? summary;
  const accountInfo = data?.account ?? account;
  const openingBank = accountInfo.openingTransferBalance ?? 0;
  const openingCash = accountInfo.openingCashBalance ?? 0;
  const closingBank = accountInfo.transferBalance ?? 0;
  const closingCash = accountInfo.cashBalance ?? 0;

  const exportReport = useCallback(async () => {
    if (!data?.rows.length || !data.summary) {
      toast.error('Không có dữ liệu để xuất báo cáo');
      return;
    }
    try {
      exportProjectAccountLedgerReport({
        accountName: accountInfo.name,
        buildingName: buildingName || 'toa-nha',
        filters,
        rows: data.rows,
        summary: periodSummary ?? data.summary,
        openingBank,
        openingCash,
        closingBank,
        closingCash,
      });
      toast.success('Đã xuất báo cáo');
    } catch {
      toast.error('Không thể xuất báo cáo');
    }
  }, [
    accountInfo.name,
    buildingName,
    filters,
    data,
    periodSummary,
    openingBank,
    openingCash,
    closingBank,
    closingCash,
  ]);

  useImperativeHandle(ref, () => ({ exportReport }), [exportReport]);

  return (
    <div className="space-y-4">
      <button type="button" onClick={onBack} className="text-cyan-600 text-sm">← Quay lại</button>

      <div className="flex gap-2 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setDetailSection('ledger')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            detailSection === 'ledger'
              ? 'border-cyan-600 text-cyan-700'
              : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          Chi tiết thu chi
        </button>
        <button
          type="button"
          onClick={() => setDetailSection('subledger')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            detailSection === 'subledger'
              ? 'border-cyan-600 text-cyan-700'
              : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          Sổ phụ tài khoản
        </button>
      </div>

      {detailSection === 'subledger' ? (
        <ProjectBankSubLedgerPanel account={account} buildingId={buildingId} />
      ) : (
        <>
      <div className="bg-white border rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-1">CHI TIẾT THU CHI</h3>
        <p className="text-sm text-gray-600">{accountInfo.name}</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4 text-sm">
          <div className="rounded-lg border p-3">
            <div className="text-gray-500">Đầu kỳ</div>
            <div>Ngân hàng: {formatCurrency(openingBank)}</div>
            <div>Tiền mặt: {formatCurrency(openingCash)}</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-gray-500">Tăng trong kỳ</div>
            <div>Ngân hàng: {formatCurrency(periodSummary?.bankIncome ?? 0)}</div>
            <div>Tiền mặt: {formatCurrency(periodSummary?.cashIncome ?? 0)}</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-gray-500">Giảm trong kỳ</div>
            <div>Ngân hàng: {formatCurrency(periodSummary?.bankExpense ?? 0)}</div>
            <div>Tiền mặt: {formatCurrency(periodSummary?.cashExpense ?? 0)}</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-gray-500">Cuối kỳ</div>
            <div>Ngân hàng: {formatCurrency(closingBank)}</div>
            <div>Tiền mặt: {formatCurrency(closingCash)}</div>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <input
            type="text"
            placeholder="Căn hộ"
            value={draftFilters.apartmentSearch || ''}
            onChange={(e) => setDraftFilters((f) => ({ ...f, apartmentSearch: e.target.value }))}
            className="rounded-lg border px-3 py-2 text-sm"
          />
          <input
            type="text"
            placeholder="Mã phiếu"
            value={draftFilters.voucherSearch || ''}
            onChange={(e) => setDraftFilters((f) => ({ ...f, voucherSearch: e.target.value }))}
            className="rounded-lg border px-3 py-2 text-sm"
          />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="">Loại</option>
            {categoryOptions.map((c) => (
              <option key={c.key} value={c.key}>{c.name}</option>
            ))}
          </select>
          <select
            value={draftFilters.createdById || ''}
            onChange={(e) => setDraftFilters((f) => ({ ...f, createdById: e.target.value || undefined }))}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="">Người thực hiện</option>
            {(data?.performers || []).map((p) => (
              <option key={p.id} value={p.id}>{p.name || p.id}</option>
            ))}
          </select>
          <input
            type="date"
            value={draftFilters.dateFrom || ''}
            onChange={(e) => setDraftFilters((f) => ({ ...f, dateFrom: e.target.value }))}
            className="rounded-lg border px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={draftFilters.dateTo || ''}
            onChange={(e) => setDraftFilters((f) => ({ ...f, dateTo: e.target.value }))}
            className="rounded-lg border px-3 py-2 text-sm"
          />
          <select
            value={draftFilters.paymentForm || ''}
            onChange={(e) =>
              setDraftFilters((f) => ({
                ...f,
                paymentForm: (e.target.value as 'CASH' | 'TRANSFER') || undefined,
              }))
            }
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="">Hình thức</option>
            <option value="CASH">Tiền mặt</option>
            <option value="TRANSFER">Chuyển khoản</option>
          </select>
          <button
            type="button"
            onClick={applyFilters}
            className="rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm px-4 py-2"
          >
            Lọc
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Ngày</th>
              <th className="px-3 py-2 text-left">Người thực hiện</th>
              <th className="px-3 py-2 text-left">Loại</th>
              <th className="px-3 py-2 text-left">Tên phiếu</th>
              <th className="px-3 py-2 text-left">Căn hộ</th>
              <th className="px-3 py-2 text-right">Dự thu</th>
              <th className="px-3 py-2 text-right">Dự chi</th>
              <th className="px-3 py-2 text-right" colSpan={2}>Ngân hàng</th>
              <th className="px-3 py-2 text-right" colSpan={2}>Tiền mặt</th>
              <th className="px-3 py-2 text-left min-w-[220px] w-[28%]">Diễn giải</th>
              <th className="px-3 py-2 text-center">Thao tác</th>
            </tr>
            <tr className="bg-gray-50 text-xs text-gray-500">
              <th colSpan={7}></th>
              <th className="px-3 py-1 text-right">Thu</th>
              <th className="px-3 py-1 text-right">Chi</th>
              <th className="px-3 py-1 text-right">Thu</th>
              <th className="px-3 py-1 text-right">Chi</th>
              <th colSpan={2}></th>
            </tr>
            {summary && (
              <tr className="bg-gray-100 font-medium">
                <td colSpan={5} className="px-3 py-2">Tổng cộng</td>
                <td className="px-3 py-2 text-right">{formatCurrency(summary.expectedIncome)}</td>
                <td className="px-3 py-2 text-right">{formatCurrency(summary.expectedExpenditure)}</td>
                <td className="px-3 py-2 text-right"><AmountCell value={summary.bankIncome} variant="income" /></td>
                <td className="px-3 py-2 text-right"><AmountCell value={summary.bankExpense} variant="expense" /></td>
                <td className="px-3 py-2 text-right"><AmountCell value={summary.cashIncome} variant="income" /></td>
                <td className="px-3 py-2 text-right"><AmountCell value={summary.cashExpense} variant="expense" /></td>
                <td colSpan={2}></td>
              </tr>
            )}
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={13} className="px-4 py-10 text-center text-gray-500">Đang tải...</td></tr>
            ) : !data?.rows.length ? (
              <tr><td colSpan={13} className="px-4 py-10 text-center text-gray-500">Không có dữ liệu</td></tr>
            ) : (
              data.rows.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(row.actualDate)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{row.performerName || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{row.typeName}</td>
                  <td className="px-3 py-2">
                    <VoucherNameCell
                      name={row.voucherName}
                      receiptRef={row.voucherReceiptRef}
                    />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {row.apartmentCode
                      ? row.apartmentCustomerCode
                        ? `${row.apartmentCode} (${row.apartmentCustomerCode})`
                        : row.apartmentCode
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-right"><AmountCell value={row.expectedIncome} variant="income" /></td>
                  <td className="px-3 py-2 text-right"><AmountCell value={row.expectedExpenditure} variant="expense" /></td>
                  <td className="px-3 py-2 text-right"><AmountCell value={row.bankIncome} variant="income" /></td>
                  <td className="px-3 py-2 text-right"><AmountCell value={row.bankExpense} variant="expense" /></td>
                  <td className="px-3 py-2 text-right"><AmountCell value={row.cashIncome} variant="income" /></td>
                  <td className="px-3 py-2 text-right"><AmountCell value={row.cashExpense} variant="expense" /></td>
                  <td className="px-3 py-2 min-w-[220px] max-w-[320px] whitespace-normal break-words align-top">{row.description}</td>
                  <td className="px-3 py-2 text-center whitespace-nowrap">
                    {row.viewType ? (
                      <div className="flex flex-col items-center gap-1">
                        <button
                          type="button"
                          onClick={() =>
                            setVoucherPreview({
                              entryId: row.id,
                              title: resolveVoucherTitle(row),
                            })
                          }
                          className="text-cyan-600 hover:text-cyan-800 text-sm font-medium"
                        >
                          Xem
                        </button>
                        {row.alternateViewType === 'surplus_receipt' && (
                          <button
                            type="button"
                            onClick={() =>
                              setVoucherPreview({
                                entryId: row.id,
                                title: 'Phiếu thu (thu dư)',
                                mode: 'surplus',
                              })
                            }
                            className="text-xs text-gray-600 hover:text-gray-800"
                          >
                            Phiếu thu dư
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {voucherPreview && (
        <LedgerVoucherModal
          entryId={voucherPreview.entryId}
          title={voucherPreview.title}
          mode={voucherPreview.mode}
          onClose={() => setVoucherPreview(null)}
        />
      )}
        </>
      )}
    </div>
  );
});

export default ProjectAccountLedgerDetail;
